<script setup lang="ts">
import { format as formatDate } from "date-fns";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";

import { useUser } from "@/composables/api/useUser";
import type { components } from "@/types/openapi.interfaces";

import PiggyBankIcon from "@/components/icons/PiggyBankIcon.vue";
import TicketIcon from "@/components/icons/TicketIcon.vue";
import ReceiptStatusBadge from "@/components/sections/receipt-details/ReceiptStatusBadge.vue";

const props = defineProps<{
  receipt: components["schemas"]["RetrieveReceipt"];
}>();

const { data: user } = useUser();

const totalPoints = computed(() =>
  props.receipt.lines.reduce((acc, line) => acc + line.loyalty_points, 0),
);

const basePoints = computed(
  () => props.receipt.loyalty_points - props.receipt.loyalty_points_boost,
);

const hasBoost = computed(
  () =>
    props.receipt.loyalty_points_boost &&
    props.receipt.loyalty_points_boost > 0 &&
    props.receipt.status !== "declared",
);
</script>

<template>
  <section class="relative flex flex-col gap-5 lg:gap-6" aria-labelledby="ticket-title">
    <ReceiptStatusBadge
      :status="receipt.status"
      class="absolute top-[-5px] right-[-30px] lg:top-[-4px] lg:right-0"
    />

    <h1
      id="ticket-title"
      :class="
        twMerge(
          'text-galbani text-lg font-semibold lg:text-[32px]',
          user?.current_mechanic === 'lactel' && 'text-lactel-blue',
        )
      "
    >
      {{ formatDate(receipt.created, "dd.MM.yy") }}
    </h1>

    <div class="flex flex-col gap-1.5 lg:gap-2">
      <h2 class="text-secondary font-medium lg:text-lg">
        {{ $t("receipt.yourGains") }}
      </h2>

      <div class="flex items-center gap-5 lg:gap-8" role="list" aria-label="Résumé des gains">
        <div class="flex items-center gap-2 lg:gap-3" role="listitem">
          <span
            :class="
              twMerge(
                'bg-galbani flex size-8 items-center justify-center rounded-full text-white lg:size-12',
                user?.current_mechanic === 'lactel' && 'bg-lactel-blue-400',
              )
            "
            aria-hidden="true"
          >
            <TicketIcon class="size-6" />
          </span>

          <p class="text-primary text-lg font-medium lg:text-2xl">
            <template v-if="receipt.status === 'declared'">
              {{ $t("receipt.vouchersPending") }}
            </template>

            <template v-else>
              {{ $t("receipt.vouchers", { n: receipt.discount_count }) }}
            </template>
          </p>
        </div>

        <div class="flex items-center gap-2 lg:gap-3" role="listitem">
          <span
            :class="
              twMerge(
                'bg-galbani flex size-8 items-center justify-center rounded-full text-white lg:size-12',
                user?.current_mechanic === 'lactel' && 'bg-lactel-blue-400',
              )
            "
            aria-hidden="true"
          >
            <PiggyBankIcon class="size-6" />
          </span>

          <p class="text-primary text-lg font-medium lg:text-2xl">
            <template v-if="receipt.status === 'declared'">
              {{ $t("receipt.pointsPending") }}
            </template>

            <template v-else>
              {{ $t("receipt.points", { n: hasBoost ? basePoints : totalPoints }) }}
            </template>
          </p>
        </div>

        <div
          v-if="hasBoost"
          :class="
            twMerge(
              'flex flex-1 items-center gap-2 rounded-lg bg-[#FEB927] py-1.5 pr-3 pl-3 lg:gap-3',
              user?.current_mechanic === 'lactel' && 'bg-lactel-yellow',
            )
          "
          role="listitem"
        >
          <div class="relative flex size-12 shrink-0 items-center justify-center rounded-full p-2">
            <span class="flex shrink-0 items-center" aria-hidden="true">
              <PiggyBankIcon
                :class="
                  twMerge(
                    'size-8 text-[#214C9F]',
                    user?.current_mechanic === 'lactel' && 'text-lactel-blue',
                  )
                "
              />
            </span>

            <span
              :class="
                twMerge(
                  'absolute top-[2px] left-[16.5px] flex size-4 items-center justify-center rounded-full bg-[#214C9F] text-white',
                  user?.current_mechanic === 'lactel' && 'bg-lactel-blue',
                )
              "
              aria-label="Bonus"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                class="size-3"
              >
                <path
                  d="M8.75 3.75a.75.75 0 0 0-1.5 0v3.5h-3.5a.75.75 0 0 0 0 1.5h3.5v3.5a.75.75 0 0 0 1.5 0v-3.5h3.5a.75.75 0 0 0 0-1.5h-3.5v-3.5Z"
                />
              </svg>
            </span>
          </div>

          <p
            :class="
              twMerge(
                'text-galbani flex-1 text-base font-bold lg:text-2xl',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
          >
            {{ $t("receipt.bonusTag", { boost: receipt.loyalty_points_boost }) }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
